import React, { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { createGlobalStyle } from "styled-components";
import { taxDeclarationAPI, type DeclaredVia, type TaxDeclaration } from "@/api/taxDeclaration";
import { DynamicModal } from "@/shared/components";
import { getApiErrorMessage } from "@/shared/utils/apiError";
import { phoneCountries } from "@/shared/utils/phoneCountries";
import { useCountryTaxConfig } from "../hooks/useCountryTaxConfig";
import { notifyCreatorTaxDeclarationUpdated } from "../utils/events";
import {
	EMPTY_TAX_DETAILS,
	toDeclarationInput,
	validateTaxDetails,
	type TaxDetailsErrors,
	type TaxDetailsValues,
} from "../utils/taxDetails";
import { TaxDetailsFields } from "./TaxDetailsFields";

interface Props {
	open: boolean;
	declaredVia: DeclaredVia;
	/** The live declaration, to prefill; null when the creator has not declared yet. */
	current: TaxDeclaration | null;
	/** Default country when there is no declaration (e.g. the address country). */
	defaultCountryCode?: string | null;
	onClose: () => void;
	onSaved: (declaration: TaxDeclaration) => void;
}

// DynamicModal's Dialog renders at z-index 2201. AppSelect's dropdown portals to
// document.body via getPopupContainer at antd's default z-index (1050), which sits
// behind the modal's own mask — the dropdown opens invisibly, and a click meant for
// an option instead hits the mask underneath and closes the modal. Same fix as
// CreatorOnboardingWizard's ONBOARDING_POPUP_CLASS, scoped to this modal's Selects.
const TAX_MODAL_POPUP_CLASS = "tax-details-modal-popup";
const TAX_MODAL_POPUP_Z_INDEX = 2210;
const TaxModalPopupStyles = createGlobalStyle`
	.${TAX_MODAL_POPUP_CLASS}.ant-select-dropdown {
		z-index: ${TAX_MODAL_POPUP_Z_INDEX} !important;
	}
`;

const fromDeclaration = (current: TaxDeclaration | null, defaultCountryCode?: string | null): TaxDetailsValues => ({
	...EMPTY_TAX_DETAILS,
	countryCode: current?.countryCode ?? defaultCountryCode ?? "",
	entityType: current?.entityType === "company" ? "company" : current ? "individual" : "",
	legalName: current?.legalName ?? "",
	isTaxRegistered: current?.isTaxRegistered ?? false,
});

/** Captures or re-confirms the tax declaration (profile page, terms acceptance). Append-only on the backend. */
export const TaxDetailsModal: React.FC<Props> = ({
	open,
	declaredVia,
	current,
	defaultCountryCode,
	onClose,
	onSaved,
}) => {
	const [values, setValues] = useState<TaxDetailsValues>(() => fromDeclaration(current, defaultCountryCode));
	const [errors, setErrors] = useState<TaxDetailsErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const { config, loading: configLoading } = useCountryTaxConfig(values.countryCode);
	const countryName = useMemo(
		() => phoneCountries.find((c) => c.iso === values.countryCode)?.name ?? values.countryCode,
		[values.countryCode],
	);

	useEffect(() => {
		if (open) {
			setValues(fromDeclaration(current, defaultCountryCode));
			setErrors({});
			setSubmitting(false);
		}
	}, [open, current, defaultCountryCode]);

	const update = <K extends keyof TaxDetailsValues>(field: K, value: TaxDetailsValues[K]) => {
		setErrors((e) => ({ ...e, [field]: undefined }));
		setValues((v) => ({ ...v, [field]: value }));
	};

	// A registered creator who already has a number on file may keep it by leaving the field blank.
	const keepsExistingNumber = Boolean(
		current?.isTaxRegistered &&
			current.registrationNumberLast4 &&
			values.isTaxRegistered &&
			!values.registrationNumber.trim() &&
			current.countryCode === values.countryCode,
	);

	const submit = async () => {
		const found = validateTaxDetails(values, config);
		if (keepsExistingNumber) delete found.registrationNumber;
		setErrors(found);
		if (Object.keys(found).length) return;
		if (keepsExistingNumber) {
			const before = fromDeclaration(current, defaultCountryCode);
			const unchanged =
				before.countryCode === values.countryCode &&
				before.entityType === values.entityType &&
				before.legalName.trim() === values.legalName.trim();
			if (unchanged) {
				// Nothing changed and the number stays on file: the live declaration stands as confirmed.
				message.success("Tax details confirmed.");
				onClose();
				return;
			}
			setErrors({
				registrationNumber: "Enter the full registration number to save these changes; it is never shown back.",
			});
			return;
		}
		setSubmitting(true);
		try {
			const saved = await taxDeclarationAPI.declare(toDeclarationInput(values, declaredVia));
			notifyCreatorTaxDeclarationUpdated();
			message.success("Tax details saved.");
			onSaved(saved);
			onClose();
		} catch (error) {
			message.error(getApiErrorMessage(error, "Unable to save your tax details."));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<TaxModalPopupStyles />
			<DynamicModal
				open={open}
				onCancel={onClose}
				title={current ? "Confirm your tax details" : "Add your tax details"}
				description="These appear on every Payment Statement issued to you. Changes apply to future statements only."
				confirmText={current ? "Confirm" : "Save"}
				cancelText="Back"
				onConfirm={() => void submit()}
				confirmDisabled={submitting || configLoading}
				loading={submitting}
				closable={!submitting}
				width={640}
				scrollable
			>
				<TaxDetailsFields
					idPrefix="tax-modal"
					values={values}
					errors={errors}
					config={config}
					configLoading={configLoading}
					countryName={countryName}
					onChange={update}
					existingNumberLast4={
						current?.countryCode === values.countryCode ? current?.registrationNumberLast4 : null
					}
					disabled={submitting}
					popupClassName={TAX_MODAL_POPUP_CLASS}
				/>
			</DynamicModal>
		</>
	);
};
